import * as React from 'react';
import { Flex, FlexItem } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { SingleColumnLayout } from '../../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../../designSystem/LoadingPageOverlay';
import { H1, H2, H3, H4 } from '../../../../designSystem/typography/Heading';
import { AdminTab } from '../../_components/AdminTab';
import { AmorgAdminPageProvider } from '../../AmorgAdminPageContext';
import { ArticlesTab } from '../_components/ArticlesTab';
import { articlesClientApi } from '../../../../api/articlesClientApi';
import * as dateMath from 'date-arithmetic';
import { toDateString } from '../../../../utils/date';
import { Button } from '../../../../designSystem/button/Button';
import { AnimatedCollapsibleContent } from '../../../../designSystem/collapsible/AnimatedCollapsibleContent';
import { ListRow } from '../../../../designSystem/compositeComponents/ListRow';
import { colors } from '../../../../designSystem/colors/color';
import { Pill } from '../../../../designSystem/pill/Pill';
import { Link } from '../../../../designSystem/Link';
import { RetsinfoChange, RetsinfoHarvestRecord, RetsinfoRecordFlagValue } from '../../../../amorg-common';
import { iconName } from '../../../../designSystem/icons/Icons';
import { TextAvatar } from '../../../../designSystem/avatar/TextAvatar';
import { RetsinfoReasonForChange } from '../../../../amorg-common';


export default function ArticlesRetsinfoOverviewPage (): JSX.Element 
{
    const [loading, setLoading] = React.useState<boolean>(true);
    const [selectedMonth, setSelectedMonth] = React.useState<Date>(new Date());
    const [records, setRecords] = React.useState<RetsinfoHarvestRecord[]>([]);
    const [showHidden, setShowHidden] = React.useState<boolean>(false);

    const [cooldown, setCooldown] = React.useState<number>(0);

    React.useEffect(() =>
    {
        (async () =>
        {
            const records = await articlesClientApi.getRetsinfoRecords(selectedMonth);
            setRecords(records);
            setLoading(false);
        })();
    }, []);

    React.useEffect(() =>
    {
        if (cooldown > 0)
        {
            setTimeout(() =>
            {
                setCooldown(cooldown - 1);
            }, 1000);
        }
    }, [cooldown]);

    async function startCooldown ()
    {
        setCooldown(10);
    }

    async function handleClickFetch (date: Date): Promise<void>
    {
        setLoading(true);
        await articlesClientApi.fetchRetsinfoRecords(date, false);
        startCooldown();
        const records = await articlesClientApi.getRetsinfoRecords(selectedMonth);
        setRecords(records);
        setLoading(false);
    }

    async function handleClickRefetch (date: Date): Promise<void>
    {
        setLoading(true);
        await articlesClientApi.fetchRetsinfoRecords(date, true);
        startCooldown();
        const records = await articlesClientApi.getRetsinfoRecords(selectedMonth);
        setRecords(records);
        setLoading(false);
    }

    async function handleClickRefetchDocument (recordId: string, changeIndex: number): Promise<void>
    {
        setLoading(true);
        await articlesClientApi.fetchRetsinfoRecordChangeDoc(recordId, changeIndex);
        const records = await articlesClientApi.getRetsinfoRecords(selectedMonth);
        setRecords(records);
        setLoading(false);
    }

    async function handleClickMonth (increment: number)
    {
        setLoading(true);
        const newMonth = dateMath.add(selectedMonth, increment, 'month');
        setSelectedMonth(newMonth);
        const records = await articlesClientApi.getRetsinfoRecords(newMonth);
        setRecords(records);
        setLoading(false);
    }

    async function handleClickSetFlag (id: string, changeIndex: number, flagValue: RetsinfoRecordFlagValue)
    {
        setLoading(true);
        await articlesClientApi.updateRetsinfoRecordFlag(id, changeIndex, flagValue);
        const updatedRecords = await articlesClientApi.getRetsinfoRecords(selectedMonth);
        setRecords(updatedRecords);
        setLoading(false);
    }

    const monthData: { date: Date, record: RetsinfoHarvestRecord | null; }[] = [];
    let current = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1, 12);
    const end = dateMath.add(current, 1, 'month');
    while (current < end)
    {
        const currentRecord = records.find(r =>
            r.recordYear === current.getFullYear() &&
            r.recordMonth === (current.getMonth() + 1) &&
            r.recordDay === current.getDate()
        );

        monthData.push({
            date: new Date(current),
            record: currentRecord || null
        });
        current = dateMath.add(current, 1, 'day');
    }

    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={false} />
            <SingleColumnLayout
                wide
                noDivider
                headerContainer={
                    <Flex flexDirection='column' gap={16}>
                        <AdminTab selectedTab='articles' />
                        <ArticlesTab selectedTab='retsinfo' />
                    </Flex>
                }

                contentContainer={

                    <Flex flexDirection='column' gap={16}>

                        <LoadingPageOverlay loading={loading} />

                        <H1>Retsinfo</H1>
                        cooldown {cooldown ? 'true' : 'false'}

                        <Flex justifyContent='center'>
                            <Button
                                variant='text'
                                icon='arrows_chevron_chevronBack'
                                onClick={() => handleClickMonth(-1)}
                            />

                            <H2>{selectedMonth.getFullYear()}-{(selectedMonth.getMonth() + 1).toString().padStart(2, '0')}</H2>

                            <Button
                                variant='text'
                                icon='arrows_chevron_chevronForward'
                                onClick={() => handleClickMonth(1)}
                            />
                        </Flex>

                        <Button variant={showHidden ? 'filled' : 'outlined'} text={showHidden ? 'Skjul gemte' : 'Vis gemte'} onClick={() => setShowHidden(!showHidden)} />

                        {monthData.filter(dateRecord => dateRecord.date <= new Date())
                            .map((recordDate) => (
                                <DateRecordPanel
                                    key={toDateString(recordDate.date)}
                                    date={recordDate.date}
                                    cooldown={cooldown}
                                    record={recordDate.record}
                                    showHidden={showHidden}
                                    onClickFetch={handleClickFetch}
                                    onClickRefetch={handleClickRefetch}
                                    onClickRefetchDocument={(recordId, changeIndex) => handleClickRefetchDocument(recordId, changeIndex)}
                                    onClickSetFlag={(changeIndex, flagValue) => handleClickSetFlag(recordDate.record?.id || '', changeIndex, flagValue)}
                                />
                            ))}

                    </Flex>
                } />

        </AmorgAdminPageProvider>
    );
}


type DateRecordPanelProps = {
    date: Date,
    cooldown: number,
    showHidden: boolean,
    record: RetsinfoHarvestRecord | null,
    onClickFetch: (date: Date) => Promise<void>,
    onClickRefetch: (date: Date) => Promise<void>,
    onClickRefetchDocument: (recordId: string, changeIndex: number) => void,
    onClickSetFlag: (changeIndex: number, flagValue: RetsinfoRecordFlagValue) => void;
};
function DateRecordPanel (props: DateRecordPanelProps): JSX.Element
{
    const [expanded, setExpanded] = React.useState<boolean>(false);

    const currentDate = props.date;
    const now = new Date();
    const outdatedDate = dateMath.add(now, -10, 'day');

    let state: 'no-record-outdated' | 'no-record' | 'no-changes' | 'has-changes' | 'has-changes-outdated' = 'no-record-outdated';
    if (props.record === null)
    {
        if (currentDate < outdatedDate)
        {
            state = 'no-record-outdated';
        }
        else
        {
            state = 'no-record';
        }
    }
    else if (props.record.changes.length === 0)
    {
        state = 'no-changes';
    }
    else
    {
        if (currentDate < outdatedDate)
            state = 'has-changes-outdated';
        else
            state = 'has-changes';
    }

    let stripColor: string | undefined = undefined;
    if (state === 'no-record-outdated')
        stripColor = undefined;
    else if (state === 'no-record')
        stripColor = colors.icon.warning;
    else if (state === 'no-changes')
        stripColor = colors.icon.passive;
    else
        stripColor = colors.icon.active;

    const hiddenChangesCount = props.record?.changes.filter(change => change.flag === 'hide').length || 0;
    const unhiddenChangesCount = props.record?.changes.filter(change => change.flag !== 'hide').length || 0;

    return (
        <ListRow key={props.date.toString()} styleVariant='neutral'
            stripColor={stripColor}>

            <Flex flexDirection="column" gap={8}>

                <H3>{toDateString(props.date)}</H3>

                <Flex justifyContent='space-between' alignItems='center' gap={16}>
                    <div>

                        {state === 'no-record-outdated' &&
                            <div>Data kan ikke længere tilgås</div>
                        }

                        {state === 'no-record' &&
                            <Flex gap={8} alignItems='center'>
                                <div>Ingen data hentet for denne dato endnu</div>
                                <CooldownButton cooldown={props.cooldown} icon='interface_actions_download' toolTipText='Hent data' onClick={() => props.onClickFetch(props.date)} />
                            </Flex>
                        }

                        {state === 'no-changes' &&
                            <div>Ingen ændringer for denne dato</div>
                        }

                        {(state === 'has-changes' || state === 'has-changes-outdated') &&
                            <Flex gap={8} alignItems='center'>
                                <TextAvatar size='24px' backgroundColor={colors.icon.active } text={unhiddenChangesCount.toString()} /> ændringer(er)
                                {hiddenChangesCount > 0 &&
                                    <>
                                        <TextAvatar size='24px' backgroundColor={colors.icon.passive } text={hiddenChangesCount.toString()} /> skjult
                                    </>
                                }
                            </Flex>
                        }
                    </div>

                    <Flex gap={8} alignItems='center'>
                        {state === 'has-changes' &&
                            <CooldownButton cooldown={props.cooldown} icon='interface_actions_synchronize' toolTipText='Genindlæs data' onClick={() => props.onClickRefetch(props.date)} />
                        }
                        {(state === 'has-changes' || state === 'has-changes-outdated') &&
                            <Button variant='outlined' icon={expanded ? 'caret_caretUp' : 'caret_caretDown'} toolTipText={expanded ? 'Skjul dokumenter' : 'Vis dokumenter'} onClick={() => setExpanded(!expanded)} />
                        }
                    </Flex>
                </Flex>

                {(state === 'has-changes' || state === 'has-changes-outdated') &&
                    <AnimatedCollapsibleContent open={expanded}>
                        <Flex flexDirection='column' gap={8}>
                            {props.record.changes.map((change, index) =>
                            {
                                if (props.showHidden || change.flag !== 'hide')
                                {

                                    const key = `${change.changeData.documentId}-${change.changeData.accessionsnummer}`;

                                    return (
                                        <ListRow key={key} styleVariant='neutral'>
                                            <RetsInfoDocumentPanel
                                                change={change}
                                                changeIndex={index}
                                                onClickRefetch={() => props.onClickRefetchDocument(props.record.id, index)}
                                                onClickSetFlag={(value) => props.onClickSetFlag(index, value)} />
                                        </ListRow>
                                    );
                                }
                            })}
                        </Flex>
                    </AnimatedCollapsibleContent>
                }
            </Flex>
        </ListRow>
    );
}

type RetsInfoDocumentPanelProps = {
    change: RetsinfoChange,
    changeIndex: number,
    onClickRefetch: () => void,
    onClickSetFlag: (flagValue: RetsinfoRecordFlagValue) => void;
};
function RetsInfoDocumentPanel ({ change, changeIndex, onClickRefetch, onClickSetFlag }: RetsInfoDocumentPanelProps): JSX.Element
{
    const [showImages, setShowImages] = React.useState<boolean>(false);

    function reasonText (reason: RetsinfoReasonForChange)
    {
        if (reason === 'Unknown')
            return 'Ukendt';
        if (reason === 'DocumentMetadataChanged')
            return 'Metadata ændret';
        if (reason === 'DocumentContentChanged')
            return 'Indhold ændret';
        if (reason === 'DocumentMetadataChangedAndDocumentContentChanged')
            return 'Metadata og indhold ændret';
        if (reason === 'NewDocument')
            return 'Nyt dokument';
        if (reason === 'RemovedDocument')
            return 'Dokument fjernet';
        return 'Ukendt årsag';
    }

    const htmlLink = change.changeData.href.replace('xml', 'html');

    const baseIdentifier = change.changeData.accessionsnummer;

    const importantChangeTypes: RetsinfoReasonForChange[] = [
        'Unknown',
        'DocumentContentChanged',
        'DocumentMetadataChangedAndDocumentContentChanged',
        'NewDocument',
        'RemovedDocument'
    ];

    return (
        <Flex flexDirection='column'>

            {change.documentData.status === 'error' &&
                <Flex flexDirection='column' gap={8}>
                    <H4 style={{ color: colors.icon.error }}>{`#${changeIndex + 1}`} Fejl ved hentning af dokument</H4>
                    <Link href={htmlLink} target='_blank'>
                        {htmlLink}
                    </Link>
                    <Flex gap={8} alignItems='center'>
                        <FlexItem flexGrow='1'>
                            <small>{change.documentData.message}</small>
                        </FlexItem>

                        <Button variant='outlined' icon='interface_actions_download' toolTipText='Genindlæs dokument' onClick={() => onClickRefetch()} />
                    </Flex>
                </Flex>
            }

            {change.documentData.status === 'not fetched' &&
                <Flex flexDirection='column' gap={8}>
                    <H4>{`#${changeIndex + 1}`} Dokument ikke hentet</H4>
                    <Link href={htmlLink} target='_blank'>
                        {htmlLink}
                    </Link>

                    <Flex>
                        <FlexItem flexGrow='1'>
                            <Flex flexDirection='column' gap={8}>
                                <Flex gap={8} alignItems='center'>
                                    <Pill identifier={`${baseIdentifier}-${change.changeData.documentType.id}1`}>{change.changeData.documentType.shortName}</Pill>
                                    <Pill identifier={change.changeData.reasonForChange}
                                        variant={importantChangeTypes.includes(change.changeData.reasonForChange) ? 'selected' : 'default'}>

                                        {reasonText(change.changeData.reasonForChange)}

                                    </Pill>
                                </Flex>
                            </Flex>
                        </FlexItem>

                        <Flex gap={8} alignItems='center'>
                            {change.flag === 'hide' &&
                                <Button variant='outlined' text='vis' onClick={() => onClickSetFlag('none')} />
                            }
                            {change.flag === 'none' &&
                                <Button variant='destructive' text='skjul' onClick={() => onClickSetFlag('hide')} />
                            }
                            <Button variant='outlined' icon='interface_actions_download' toolTipText='Indlæs dokument' onClick={() => onClickRefetch()} />
                        </Flex>
                    </Flex>
                </Flex>
            }

            {change.documentData.status === 'fetched' &&
                <Flex flexDirection='column' gap={8}>
                    <div>
                        <H4>{`#${changeIndex + 1}`} {change.documentData.data.documentTitle}</H4>
                        <small>#{change.changeData.documentId}</small>
                    </div>

                    <Link href={htmlLink} target='_blank'>
                        {htmlLink}
                    </Link>

                    <Flex>
                        <FlexItem flexGrow='1'>
                            <Flex flexDirection='column' gap={8}>
                                <Flex gap={8} alignItems='center'>
                                    <Pill identifier={`${baseIdentifier}-${change.documentData.data.ministry}`}>{change.documentData.data.ministry}</Pill>
                                    <Pill identifier={`${baseIdentifier}-${change.changeData.documentType.id}1`}>{change.changeData.documentType.shortName}</Pill>
                                    <Pill identifier={change.changeData.reasonForChange}
                                        variant={importantChangeTypes.includes(change.changeData.reasonForChange) ? 'selected' : 'default'}>

                                        {reasonText(change.changeData.reasonForChange)}

                                    </Pill>
                                </Flex>
                            </Flex>
                        </FlexItem>

                        <div>
                            {change.flag === 'hide' &&
                                <Button variant='outlined' text='vis' onClick={() => onClickSetFlag('none')} />
                            }
                            {change.flag === 'none' &&
                                <Button variant='destructive' text='skjul' onClick={() => onClickSetFlag('hide')} />
                            }
                        </div>
                    </Flex>

                    {change.changeData.images.length > 0 &&
                        <div>
                            <Flex alignItems='center'>
                                <H4>{change.changeData.images.length} billede(r)</H4>
                                <Button variant='outlined' icon={showImages ? 'caret_caretUp' : 'caret_caretDown'}
                                    onClick={() => setShowImages(!showImages)}
                                    toolTipText={showImages ? 'Skjul billede(r)' : 'Vis billede(r)'} />
                            </Flex>

                            <AnimatedCollapsibleContent open={showImages}>
                                {change.changeData.images.map((img, index) => (
                                    <img key={`${baseIdentifier}-image-${index}`}
                                        src={img.source}
                                        alt={img.altText || 'Retsinfo image'}
                                        style={{ maxWidth: '100px', marginRight: '8px', marginBottom: '8px' }}
                                    />
                                ))}
                            </AnimatedCollapsibleContent>
                        </div>
                    }

                </Flex>
            }
        </Flex>
    );
}

type CooldownButtonProps = {
    icon: iconName,
    toolTipText: string,
    onClick: () => void,
    cooldown: number;
};
function CooldownButton (props: CooldownButtonProps): JSX.Element
{
    return (
        <Button
            variant='outlined'
            text={props.cooldown > 0 ? props.cooldown.toString() : undefined}
            icon={props.icon}
            toolTipText={props.cooldown ? 'Retsinfo kan kun tilgås hvert 10. sekund - afventer' : props.toolTipText}
            disabled={props.cooldown > 0}
            onClick={props.onClick}
        />
    );
}